import React from 'react';
import {
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  Stack,
  Text,
  Textarea,
  useDisclosure,
} from '@chakra-ui/react';
import { Warning } from 'phosphor-react';
import type { ISecaoFicha } from '../../../../hooks/useEventoContrato';
import { editadaNaTela, estadoDaSecao, papelDaSecao, textoDaSecao } from './secoesDaFicha';

interface Props {
  secoes: ISecaoFicha[];
  /** chave -> texto editado na tela (ainda não recarregado do servidor). */
  editadas: Record<string, string>;
  bloqueado: boolean;
  onChange: (chave: string, texto: string) => void;
}

/** Linhas visíveis pelo tamanho do texto, não só pelo Enter: parágrafo longo quebra em várias. */
const linhasVisiveis = (texto: string): number =>
  Math.min(
    14,
    Math.max(
      4,
      texto.split('\n').reduce((total, l) => total + Math.max(1, Math.ceil(l.length / 90)), 0) + 1,
    ),
  );

function Secao({
  s,
  texto,
  editado,
  bloqueado,
  onChange,
}: {
  s: ISecaoFicha;
  texto: string;
  editado: boolean;
  bloqueado: boolean;
  onChange: (texto: string) => void;
}) {
  const modelo = useDisclosure();
  const estado = estadoDaSecao(s, editado);
  const id = `campo-${papelDaSecao(s.chave)}`;
  return (
    <Box id={id} borderWidth="1px" borderColor="gray.100" borderRadius="10px" p={4}>
      <Flex align="center" gap={2} mb={2} wrap="wrap">
        <Text as="label" htmlFor={`${id}-texto`} fontWeight={600} fontSize="13px">
          {s.titulo}
        </Text>
        <Text fontSize="11px" color="gray.500">
          {s.template}
        </Text>
        <Badge colorScheme={estado.cor} fontSize="10px" textTransform="none">
          {estado.texto}
        </Badge>
      </Flex>
      {/* Fica visível durante a edição: é a lista do que corrigir. */}
      {s.conferido === false && s.motivo && (
        <Text fontSize="12px" color="red.600" mb={2}>
          {s.motivo}
        </Text>
      )}
      {!editado && s.fonte === 'operador' && !!s.numeros_sem_fonte?.length && (
        <Text fontSize="12px" color="orange.600" mb={2}>
          Número que não está nas fontes: {s.numeros_sem_fonte.join(', ')} — confira antes de gerar.
        </Text>
      )}
      {s.avisos.map(a => (
        <Flex key={a} gap={2} mb={1} align="flex-start">
          <Box color="orange.400" mt="2px">
            <Warning size={13} />
          </Box>
          <Text fontSize="12px" color="gray.700">
            {a}
          </Text>
        </Flex>
      ))}
      <Textarea
        id={`${id}-texto`}
        value={texto}
        onChange={e => onChange(e.target.value)}
        isDisabled={bloqueado}
        rows={linhasVisiveis(texto)}
        fontSize="13px"
        placeholder={
          s.removida
            ? 'A proposta não tem esta parte: a seção sai do contrato. Escreva aqui só se ela deve ficar.'
            : 'Leia as fontes para a IA redigir a seção pela proposta, ou escreva aqui.'
        }
      />
      <Flex align="center" justify="space-between" mt={1} gap={2} wrap="wrap">
        <Text fontSize="11px" color="gray.500">
          Um parágrafo por linha. Entra no lugar do texto do modelo; o resto do contrato fica.
        </Text>
        <Button size="xs" variant="link" onClick={modelo.onToggle} aria-expanded={modelo.isOpen}>
          {modelo.isOpen
            ? 'Esconder o texto do modelo'
            : 'Ver o texto do modelo (fornecedor anterior)'}
        </Button>
      </Flex>
      <Collapse in={modelo.isOpen} animateOpacity>
        <Box mt={2} p={3} bg="gray.50" borderRadius="8px">
          {s.modelo
            .filter(l => l.trim())
            .map((l, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Text key={i} fontSize="12px" color="gray.600" mb={1}>
                {l}
              </Text>
            ))}
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * Seções do negócio (parceria, bar, produto): o trecho do modelo que muda de estrutura a cada proposta — lista de
 * produtos e preços, áreas e valor por pessoa, adiantamento. A IA redige na leitura das fontes, só com números que
 * estão nelas; aqui se confere e, se precisar, reescreve antes de gerar.
 */
export default function SecoesDoNegocio({ secoes, editadas, bloqueado, onChange }: Props) {
  return (
    <Stack spacing={4}>
      {secoes.map(s => (
        <Secao
          key={s.chave}
          s={s}
          texto={editadas[s.chave] ?? textoDaSecao(s)}
          editado={editadaNaTela(editadas, s)}
          bloqueado={bloqueado}
          onChange={t => onChange(s.chave, t)}
        />
      ))}
    </Stack>
  );
}
